import React from "react";
import { axiosget, axiospost } from "../../helpers/httprequest";
import swal from "../../helpers/swal";
import states_lga from "../../data/state_lga.json";
import Loader from "../../components/Loader";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import { Form, Formik } from "formik";
import { createapplicantbiorules } from "../../utils/validations";
import PageHeader from "../../components/PageHeader";
import FormSelect from "../../components/ui/FormSelect";
import { Button } from "../../components/ui/Button";
import { FormInput } from "../../components/ui/FormInput";

const BioData = ({ wizardMode = false }) => {
  const queryClient = useQueryClient();
  const states = states_lga.map((v) => ({ label: v.state, value: v.state })).sort((a, b) => a.label.localeCompare(b.label));

  const { isLoading, error, data } = useQuery({
    queryKey: ["bioDataLoaded"],
    queryFn: async () => {
      const req = await axiosget("ms_applicant/bio");
      return req?.data;
    },
  });

  const _login = async (values) => {
    try {
      const req = await axiospost("ms_applicant/bio", {
        firstname: values?.firstname,
        middlename: values?.middlename,
        lastname: values?.lastname,
        gender: values?.gender,
        religion: values?.religion,
        dateofbirth: values?.dateofbirth,
        qualification: values?.qualification,
        maritalstatus: values?.maritalstatus,
        emailaddress: values?.emailaddress,
        phonenumber: values?.phonenumber,
        homeaddress: values?.homeaddress,
        stateoforigin: values?.stateoforigin,
        stateofresidence: values?.stateofresidence,
        lgaoforigin: values?.lgaoforigin,
        lgaofresidence: values?.lgaofresidence,
      });
      if (req.status !== 200) {
        throw new Error("Sorry, system could not finish processing your request");
      }
      const { data } = req;
      if (data && data.type === "success") {
        swal(data.type, data.message);
        queryClient.invalidateQueries({ queryKey: ["bioDataLoaded"] });
        queryClient.invalidateQueries({ queryKey: ["dashboardDataset"] });
      } else {
        throw new Error(data.message);
      }
    } catch ({ message }) {
      swal("warning", message);
    }
  };

  return (
    <>
      {!wizardMode && <PageHeader
        title="Bio Data"
        subtitle="Update your personal information"
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        }
      />}
      {isLoading && <Loader />}
      {error || (data?.message && <Alert variant="danger"><AlertDescription>{error?.message ? error?.message : data?.message}</AlertDescription></Alert>)}
      {data?.message === undefined && (
        <div>
          <Formik
            enableReinitialize={true}
            initialValues={{
              firstname: data?.firstname || "",
              middlename: data?.middlename || "",
              lastname: data?.lastname || "",
              gender: data?.gender || "",
              religion: data?.religion || "",
              qualification: data?.qualification || "",
              maritalstatus: data?.maritalstatus || "",
              emailaddress: data?.emailaddress || "",
              phonenumber: data?.phonenumber || "",
              homeaddress: data?.homeaddress || "",
              stateoforigin: data?.stateoforigin || "",
              stateofresidence: data?.stateofresidence || "",
              lgaoforigin: data?.lgaoforigin || "",
              lgaofresidence: data?.lgaofresidence || "",
              dateofbirth: data?.dateofbirth ? String(data.dateofbirth).split("T")[0] : "",
            }}
            validationSchema={createapplicantbiorules}
            onSubmit={_login}
          >
            {({ isSubmitting, values }) => (
              <Form className="space-y-3">
                {/* Personal Information */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-2">Personal Information</h3>
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <FormInput name="firstname" label="First Name" placeholder="Enter first name" />
                      <FormInput name="middlename" label="Middle Name" placeholder="Enter middle name" />
                      <FormInput name="lastname" label="Last Name" placeholder="Enter last name" />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <FormSelect
                        name="gender"
                        label="Gender"
                        placeholder="Select gender"
                        options={[
                          { label: "Male", value: "Male" },
                          { label: "Female", value: "Female" },
                        ]}
                        searchable={false}
                      />
                      <FormSelect
                        name="religion"
                        label="Religion"
                        placeholder="Select religion"
                        options={[
                          { label: "Islam", value: "Islam" },
                          { label: "Christianity", value: "Christianity" },
                          { label: "Others", value: "Others" },
                        ]}
                        searchable={false}
                      />
                      <FormInput name="dateofbirth" label="Date of Birth" type="date" />
                    </div>
                  </div>
                </div>

                <hr className="border-neutral-100" />

                {/* Contact Information */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-2">Contact Information</h3>
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FormInput
                        name="emailaddress"
                        label="Email Address"
                        type="email"
                        placeholder="Enter your email"
                        leftIcon={
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                          </svg>
                        }
                      />
                      <FormInput
                        name="phonenumber"
                        label="Phone Number"
                        placeholder="08012345678"
                        leftIcon={
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        }
                      />
                    </div>
                    <FormInput
                      name="homeaddress"
                      label="Contact Address"
                      placeholder="Enter your full address"
                      leftIcon={
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      }
                    />
                  </div>
                </div>

                <hr className="border-neutral-100" />

                {/* Location Details */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-2">Location Details</h3>
                  <div className="space-y-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FormSelect
                        name="stateoforigin"
                        label="State of Origin"
                        placeholder="Select state"
                        options={states}
                        searchable
                      />
                      <FormSelect
                        name="lgaoforigin"
                        label="LGA of Origin"
                        placeholder={values.stateoforigin ? "Select LGA" : "Select state first"}
                        disabled={!values.stateoforigin}
                        options={
                          states_lga
                            ?.find((s) => s.state === values.stateoforigin)
                            ?.lgas?.map((r) => ({ label: r, value: r })) || []
                        }
                        searchable
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <FormSelect
                        name="stateofresidence"
                        label="State of Residence"
                        placeholder="Select state"
                        options={states}
                        searchable
                      />
                      <FormSelect
                        name="lgaofresidence"
                        label="LGA of Residence"
                        placeholder={values.stateofresidence ? "Select LGA" : "Select state first"}
                        disabled={!values.stateofresidence}
                        options={
                          states_lga
                            ?.find((s) => s.state === values.stateofresidence)
                            ?.lgas?.map((r) => ({ label: r, value: r })) || []
                        }
                        searchable
                      />
                    </div>
                  </div>
                </div>

                <hr className="border-neutral-100" />

                {/* Additional Details */}
                <div>
                  <h3 className="text-sm font-semibold text-neutral-900 uppercase tracking-wide mb-2">Additional Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <FormSelect
                      name="qualification"
                      label="Highest Qualification"
                      placeholder="Select qualification"
                      options={[
                        { label: "Primary School Certificate", value: "Primary School Certificate" },
                        { label: "Secondary/O-Level Certificate", value: "Secondary/O-Level Certificate" },
                        { label: "National Diploma (ND)", value: "National Diploma (ND)" },
                        { label: "Nigeria Certificate in Education (NCE)", value: "Nigeria Certificate in Education (NCE)" },
                        { label: "Higher National Diploma (HND)", value: "Higher National Diploma (HND)" },
                        { label: "University Degree", value: "University Degree" },
                        { label: "Masters Degree", value: "Masters Degree" },
                        { label: "PhD. Degree", value: "PhD. Degree" },
                      ]}
                      searchable
                    />
                    <FormSelect
                      name="maritalstatus"
                      label="Marital Status"
                      placeholder="Select status"
                      options={[
                        { label: "Single", value: "Single" },
                        { label: "Married", value: "Married" },
                        { label: "Divorced", value: "Divorced" },
                        { label: "Widowed", value: "Widowed" },
                      ]}
                      searchable={false}
                    />
                  </div>
                </div>

                {/* Submit */}
                <div className="pt-2">
                  <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={isSubmitting}
                  >
                    {isSubmitting ? "Submitting..." : "Save Bio Data"}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      )}
    </>
  );
};

export default BioData;
